import React from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import { colors, typography } from '../../../styles/theme';
import { ContentSection, SectionTitle } from '../common/StyledComponents';

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: ${colors.background.light};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.accent}10;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const DocumentIconWrapper = styled.div`
  margin-right: 16px;
  color: ${colors.primary};
`;

const DocumentInfo = styled.div`
  flex: 1;
`;

const DocumentTitle = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  color: ${colors.text.primary};
  font-weight: ${typography.fontWeight.medium};
  margin-bottom: 4px;
`;

const DocumentDate = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
`;

const DocumentIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2"/>
    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const PolicyFiles = ({ files = [] }) => {
  const handleDownload = async (fileId) => {
    try {
      // Get the current user's auth token
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();
      
      if (!token) {
        console.error('No authentication token available');
        return;
      }

      // Create URL with auth token
      const url = `/api/dashboard/files/${fileId}`;
      
      // Open in new tab - the browser will handle the auth header
      const downloadWindow = window.open('about:blank');
      
      // Fetch the file with authentication
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      // Get the blob and create an object URL
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      
      // Update the blank window with the file
      if (downloadWindow) {
        downloadWindow.location.href = objectUrl;
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      // Here you could show a user-friendly error message
    }
  };

  return (
    <ContentSection>
      <SectionTitle>Policy Documents</SectionTitle>
      <DocumentList>
        {files.length === 0 ? (
          <DocumentItem>
            <DocumentInfo>
              <DocumentTitle>No policy documents found</DocumentTitle>
            </DocumentInfo>
          </DocumentItem>
        ) : (
          files.map((doc) => (
            <DocumentItem 
              key={doc.id}
              onClick={() => handleDownload(doc.id)}
            >
              <DocumentIconWrapper>
                <DocumentIcon />
              </DocumentIconWrapper>
              <DocumentInfo>
                <DocumentTitle>{doc.title}</DocumentTitle>
                <DocumentDate>{doc.date}</DocumentDate>
              </DocumentInfo>
            </DocumentItem>
          ))
        )}
      </DocumentList>
    </ContentSection>
  );
};

export default PolicyFiles; 